import { Badge, Button, Card, Divider, Grid, Group, Loader, Paper, Stack, Text } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import React from "react";
import { FaChartLine } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import SimpleHeader from "../components/simple_header";
import { useApp } from "../contexts/app.context";
import { GET_ALL_OAUTHS } from "../services/oauths";
import { getConnectionDisplayTitle, getCustomerSocialConnections } from "../utility/customer_social";

const reportSections = [
  { key: "overview", title: "Visão geral", path: "" },
  { key: "media", title: "Mídias", path: "/media" },
  { key: "growth", title: "Crescimento", path: "/growth" },
  { key: "engagement", title: "Engajamento", path: "/engagement" },
  { key: "best-posts", title: "Melhores posts", path: "/best-posts" },
  { key: "audience", title: "Audiência", path: "/audience" },
];

const getInstagramOauthId = (connection: any) => {
  return connection?.oauthId || connection?._id || connection?.oauth?._id || connection?.raw?._id || null;
};

export default function ReportsPage() {
  const navigate = useNavigate();
  const { customers, loadingCustomers } = useApp();

  const [loadingOauths, setLoadingOauths] = React.useState<boolean>(true);
  const [oauthMap, setOauthMap] = React.useState<Map<string, any>>(new Map());

  React.useEffect(() => {
    setLoadingOauths(true);
    GET_ALL_OAUTHS()
      .then((oauths) => {
        const instagramOauths = (oauths ?? []).filter((item) => item.origin === "instagram");
        const nextMap = new Map<string, any>();

        instagramOauths.forEach((oauth) => {
          if (oauth?._id) {
            nextMap.set(oauth._id, oauth);
          }
        });

        setOauthMap(nextMap);
      })
      .catch((err: any) => {
        showNotification({ title: "Ops", message: err.message, color: "red" });
      })
      .finally(() => {
        setLoadingOauths(false);
      });
  }, []);

  const activeCustomers = React.useMemo(() => {
    return (customers ?? []).filter((customer) => customer?.active);
  }, [customers]);

  const customerReports = React.useMemo(() => {
    return activeCustomers
      .map((customer) => {
        const connections = getCustomerSocialConnections(customer).filter((connection: any) => connection?.origin === "instagram");

        const normalizedConnections = connections
          .map((connection: any) => {
            const oauthId = getInstagramOauthId(connection);
            const oauth = oauthMap.get(oauthId) || connection?.oauth || null;

            if (!oauthId) {
              return null;
            }

            return {
              oauthId,
              oauth,
              label: getConnectionDisplayTitle(
                {
                  ...connection,
                  data: oauth?.data || connection?.data,
                },
                "Conta Instagram"
              ),
            };
          })
          .filter((item): item is { oauthId: string; oauth: any; label: string } => !!item);

        return {
          customer,
          connections: normalizedConnections,
        };
      })
      .filter((item): item is { customer: any; connections: { oauthId: string; oauth: any; label: string }[] } => !!item);
  }, [activeCustomers, oauthMap]);

  const loading = loadingCustomers || loadingOauths;

  return (
    <div style={{ position: "relative" }}>
      <SimpleHeader
        title="Relatórios"
        subtitle="Relatórios de Instagram e performance do workflow"
        Icon={FaChartLine}
        right={
          <Button variant="light" onClick={() => navigate("/analytics/performance")}>
            Analytics de performance
          </Button>
        }
      />

      {loading ? (
        <Paper p="xl" shadow="xs">
          <Group position="center">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              Carregando relatórios disponíveis...
            </Text>
          </Group>
        </Paper>
      ) : customerReports.length === 0 ? (
        <Paper p="xl" shadow="xs">
          <Text align="center" c="dimmed">
            Nenhum cliente ativo disponível.
          </Text>
        </Paper>
      ) : (
        <Grid>
          {customerReports.map(({ customer, connections }) => (
            <Grid.Col xs={12} md={12} key={customer._id}>
              <Card withBorder h="100%">
                <Group position="apart" mb="md">
                  <Stack spacing={2}>
                    <Text fw={700}>{customer?.name || "Cliente"}</Text>
                    <Text size="xs" c="dimmed">
                      {connections.length > 0
                        ? `${connections.length} conta(s) Instagram conectada(s)`
                        : "Sem Instagram conectado, com analytics de workflow disponível"}
                    </Text>
                  </Stack>
                  <Group spacing={8}>
                    <Badge color="blue">Performance</Badge>
                    {connections.length > 0 ? <Badge color="pink">Instagram</Badge> : null}
                  </Group>
                </Group>

                <Stack spacing="md">
                  <Paper withBorder p="sm">
                    <Stack spacing="xs">
                      <Text fw={600} size="sm">
                        Analytics de performance do workflow
                      </Text>
                      <Text size="xs" c="dimmed">
                        Eficiência, gargalos, retrabalho, agendamento, atrasos e throughput por cliente.
                      </Text>
                      <Group spacing={8}>
                        <Button
                          size="xs"
                          onClick={() => navigate(`/analytics/performance?customerId=${customer._id}`)}
                        >
                          Abrir performance
                        </Button>
                        <Button
                          size="xs"
                          variant="light"
                          onClick={() => navigate(`/analytics/performance?customerId=${customer._id}&detailCustomerId=${customer._id}`)}
                        >
                          Ver detalhe do cliente
                        </Button>
                      </Group>
                    </Stack>
                  </Paper>

                  {connections.length > 0 ? <Divider /> : null}

                  {connections.map((connection) => (
                    <Paper key={`${customer._id}-${connection.oauthId}`} withBorder p="sm">
                      <Text fw={600} size="sm" mb="xs">
                        {connection.label}
                      </Text>
                      <Group spacing={8}>
                        {reportSections.map((section) => (
                          <Button
                            key={`${connection.oauthId}-${section.key}`}
                            size="xs"
                            variant={section.key === "overview" ? "filled" : "light"}
                            onClick={() => navigate(`/analytics/instagram/${connection.oauthId}${section.path}`)}
                          >
                            {section.title}
                          </Button>
                        ))}
                      </Group>
                    </Paper>
                  ))}
                </Stack>
              </Card>
            </Grid.Col>
          ))}
        </Grid>
      )}
    </div>
  );
}
